import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Divider,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  LinearProgress,
  Card,
  CardActionArea,
  Chip,
  Tooltip,
  Pagination,
} from '@mui/material';
import { Inventory2Outlined, Search } from '@mui/icons-material';

export default function Cards({ plans, selected, setSelected, statusOptions }) {
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');

  const rowsPerPage = 5;
  const isSearching = searchText.trim() !== '';

  const filteredItems = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    if (!search) return plans.items ?? [];

    return (plans.items ?? []).filter(
      (item) =>
        item.plan_name?.toLowerCase().includes(search) ||
        item.plan_id?.toLowerCase().includes(search),
    );
  }, [plans.items, searchText]);

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * rowsPerPage;

    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  const handleSearch = (event) => {
    setSearchText(event.target.value);
    setPage(1);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box
        sx={{
          px: 1,
          py: 1,
        }}
      >
        <Box display='flex' alignItems='center' gap={1}>
          <Inventory2Outlined color='success' fontSize='small' />

          <Typography fontWeight={700}>Allocated Plans</Typography>

          <Typography variant='body2' color='text.secondary'>
            List of finalize plan
          </Typography>
        </Box>
      </Box>

      {/* Full-width divider */}
      <Divider sx={{ mx: -3, my: 2 }} />

      <Box>
        <TextField
          size='small'
          value={searchText}
          onChange={handleSearch}
          placeholder='Search by plan id/name '
          sx={{ width: 380 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box mt={3} pb={3}>
        <Box sx={{ width: '100%' }}>
          {plans.loading ? (
            <LinearProgress sx={{ width: '100%' }} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                pb: 1,
              }}
            >
              {paginatedItems.map((item) => {
                const active = selected === item.plan_id;

                return (
                  <Card
                    key={item.id}
                    sx={{
                      width: 425,
                      borderRadius: 2,
                      border: active ? '2px solid #43A047' : '1px solid #E0E0E0',
                      bgcolor: active ? '#F3FBF3' : '#FFF',
                      boxShadow: active
                        ? '0 3px 10px rgba(0,0,0,.12)'
                        : '0 1px 3px rgba(0,0,0,.08)',
                      transition: '.25s',
                      p: isSearching ? 2 : 1,
                    }}
                  >
                    <CardActionArea sx={{ p: 2 }} onClick={() => setSelected(item.plan_id)}>
                      <Typography
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 14,
                          color: 'text.secondary',
                        }}
                      >
                        {item.plan_id}
                      </Typography>

                      <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                        <Typography fontWeight={700} fontSize={28 / 2}>
                          {item.plan_name}
                        </Typography>

                        {item.category === 'Regular' ? (
                          <Chip
                            size='small'
                            label='● Regular'
                            sx={{
                              bgcolor: '#E8F5E9',
                              color: '#2E7D32',
                              fontWeight: 600,
                            }}
                          />
                        ) : (
                          <Chip
                            size='small'
                            label={`● Special ${item.sub_category ?? ''}`}
                            sx={{
                              bgcolor: '#FFF3E0',
                              color: '#EF6C00',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>

                      <Box display='flex' alignItems='center' gap={2} mt={0.5}>
                        <Typography variant='body2' color='text.secondary'>
                          Total Reps: {item.total_reps}
                        </Typography>

                        <Typography variant='body2' color='text.secondary'>
                          MDs: {item.total_mds}
                        </Typography>

                        <Typography variant='body2' color='text.secondary'>
                          Effectivity: {item.effectivity_month} {item.effectivity_year}
                        </Typography>
                      </Box>

                      <Box display='flex' gap={1} flexWrap='wrap' mt={2}>
                        {(() => {
                          const statuses = statusOptions
                            .filter((status) => (item.total_status?.[status.key] ?? 0) > 0)
                            .map((status) => ({
                              ...status,
                              count: item.total_status[status.key],
                            }));

                          const visibleStatuses = statuses.slice(0, 2);
                          const remainingStatuses = statuses.slice(2);

                          return (
                            <>
                              {visibleStatuses.map((status) => (
                                <Chip
                                  key={status.key}
                                  icon={status.icon}
                                  label={`${status.label} (${status.count})`}
                                  variant='outlined'
                                  size='small'
                                  sx={{
                                    color: status.color,
                                    borderColor: status.color,
                                    backgroundColor: `${status.color}15`,
                                    '& .MuiChip-icon': {
                                      color: status.color,
                                    },
                                  }}
                                />
                              ))}

                              {remainingStatuses.length > 0 && (
                                <Tooltip
                                  arrow
                                  placement='top'
                                  title={
                                    <Box display='flex' flexDirection='column' gap={0.5}>
                                      {remainingStatuses.map((status) => (
                                        <Box
                                          key={status.key}
                                          display='flex'
                                          alignItems='center'
                                          gap={0.5}
                                        >
                                          <Box
                                            sx={{
                                              width: 7,
                                              height: 7,
                                              borderRadius: '50%',
                                              backgroundColor: status.color,
                                            }}
                                          />
                                          {status.label} ({status.count})
                                        </Box>
                                      ))}
                                    </Box>
                                  }
                                >
                                  <Chip
                                    label={`+${remainingStatuses.length}`}
                                    size='small'
                                    variant='outlined'
                                    sx={{
                                      cursor: 'pointer',
                                      color: 'text.secondary',
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
      <Divider sx={{ mx: -3, my: 2 }} />
      <Box display='flex' justifyContent='flex-end' mt={1} pl={1}>
        <Pagination
          page={page}
          count={Math.ceil(filteredItems.length / rowsPerPage)}
          onChange={(e, value) => setPage(value)}
        />
      </Box>
    </Paper>
  );
}

Cards.propTypes = {
  plans: PropTypes.shape({
    loading: PropTypes.bool,
    items: PropTypes.array,
  }).isRequired,
  selected: PropTypes.object,
  setSelected: PropTypes.func.isRequired,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
      color: PropTypes.string,
      icon: PropTypes.element,
    }),
  ),
};
